import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { type DayColumnTarget, DayColumnTargets } from "@/components/day-columns";
import type { McpUsageDay } from "@/lib/api";
import {
  type ChartColumn,
  chartColumns,
  type ChartMeasure,
  type ChartSeries,
} from "@/lib/calls-chart";
import { formatDay, formatDayMonth, formatNumber, formatShare, weekdayOf } from "@/lib/format";
import { cn } from "@/lib/utils";

/** Up to this many days, every column carries its value and a day label. */
const LABELLED_DAYS = 14;

/** A day in full, for the tooltip: "do 15/10/2026", "Thu 15/10/2026". */
function dayLabel(day: string): string {
  return `${weekdayOf(day)} ${formatDay(day)}`.trim();
}

/** A column's value as its label: "46", or "3%" for a share ("<1%" for a small one, never "0%"). */
function valueText(value: number, measure: ChartMeasure): string {
  return measure === "percent" ? formatShare(value) : formatNumber(value);
}

/**
 * A day's figures in words, for its tooltip and its name: every call ("do
 * 15/10/2026: 46 oproepen, 1 fout"), or the failures and their share ("do
 * 15/10/2026: 1 fout van 46 oproepen (2%)"), or that the day had no calls.
 */
function dayDescription(t: TFunction, column: ChartColumn, series: ChartSeries): string {
  const day = dayLabel(column.day);
  if (series === "all") {
    return t("chart.dayDescription", {
      day,
      calls: t("chart.calls", { count: column.total }),
      errors: t("chart.errorCount", { count: column.errors }),
    });
  }
  if (column.share === null) {
    return t("chart.noCallsDay", { day });
  }
  return t("chart.errorDayDescription", {
    day,
    errors: t("chart.errorCount", { count: column.errors }),
    calls: t("chart.calls", { count: column.total }),
    share: formatShare(column.share),
  });
}

interface CallsChartProps {
  /** Every day of the period, zeros included: its successful and its failed calls. */
  data: readonly McpUsageDay[];
  /** A shorter plot, for the home page. */
  compact?: boolean;
  /** Every call, the failed ones on top (`all`, the default), or the failed calls alone. */
  series?: ChartSeries;
  /**
   * The failed calls alone as a count or as a percentage of that day's calls
   * (`<MeasureSwitch>` above the chart); a day without calls then has no column.
   */
  measure?: ChartMeasure;
}

/**
 * Dependency-free column chart of tool calls per day, per the De Ceuster
 * figure style: the successful calls at the base in dc-blauw-licht
 * (`chart-2`), the failed calls stacked on top in De Ceuster blauw
 * (`chart-1`, what to look at), with a 2 px gap between them, and the legend
 * in the same order, base first; square columns about 65% of their band.
 * With `series="errors"` it shows the failed calls alone, as a
 * count or a percentage of that day's calls (`measure`), and needs no legend.
 * Up to 14 days every column carries its value, so there is no value axis;
 * longer periods get one gridline at the maximum instead.
 *
 * Every day's figures show in a tooltip on hover, on a tap, and on the
 * keyboard's focus (one Tab into the chart, then the arrows), and each
 * column says them to screen readers (`<DayColumnTargets>`), so the chart
 * needs no table of figures under it. Day labels follow the dashboard's
 * Belgian form (`@/lib/format`), with the weekday and the numbers in the
 * dashboard's language.
 *
 *     <CallsChart data={usage.per_day} />
 *     <MeasureSwitch value={measure} onChange={setMeasure} />
 *     <CallsChart data={issue.per_day} series="errors" measure={measure} />
 */
export function CallsChart({
  data,
  compact = false,
  series = "all",
  measure = "count",
}: CallsChartProps) {
  const { t } = useTranslation();
  const shownMeasure: ChartMeasure = series === "errors" ? measure : "count";
  const { columns, max } = chartColumns(data, { series, measure: shownMeasure });
  const labelled = data.length <= LABELLED_DAYS;
  const sum = columns.reduce((a, c) => a + c.total, 0);
  const errors = columns.reduce((a, c) => a + c.errors, 0);
  const first = columns.at(0);
  const last = columns.at(-1);
  const targets: DayColumnTarget[] = columns.map((column) => {
    const text = dayDescription(t, column, series);
    return { day: column.day, label: text, tooltip: text };
  });

  return (
    <figure
      aria-label={t("chart.summary", {
        days: data.length,
        calls: t("chart.calls", { count: sum }),
        errors: t("chart.errorCount", { count: errors }),
      })}
    >
      {series === "all" && (
        <div className="text-foreground mb-4 flex items-center gap-4 text-xs font-semibold">
          <span className="flex items-center gap-1.5">
            <span className="bg-chart-2 inline-block h-2.5 w-2.5" /> {t("chart.successful")}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="bg-chart-1 inline-block h-2.5 w-2.5" /> {t("chart.errors")}
          </span>
        </div>
      )}
      <div className={cn("relative pt-5", compact ? "h-32" : "h-44")}>
        {!labelled && (
          <div aria-hidden="true" className="border-border absolute inset-x-0 top-5 border-t-2">
            <span className="text-muted absolute -top-5 left-0 text-xs">
              {valueText(max, shownMeasure)}
            </span>
          </div>
        )}
        <div aria-hidden="true" className="border-border h-full border-b-2">
          <svg className="h-full w-full overflow-visible">
            {columns.map(
              (column) =>
                column.value !== null &&
                // The failures alone say "0" on every day without one, also a day without
                // calls; as a percentage a day with calls says "0%", and a day without calls
                // has no column.
                (column.value > 0 ||
                  (series === "errors" && (shownMeasure === "count" || column.total > 0))) && (
                  <g key={column.day}>
                    {series === "all" && column.ok > 0 && (
                      <rect
                        x={`${column.x}%`}
                        width={`${column.width}%`}
                        y={`${column.split}%`}
                        height={`${100 - column.split}%`}
                        className="fill-chart-2"
                      />
                    )}
                    {column.errors > 0 && (
                      <rect
                        x={`${column.x}%`}
                        width={`${column.width}%`}
                        y={`${column.top}%`}
                        height={`${column.split - column.top}%`}
                        className="fill-chart-1"
                      />
                    )}
                    {series === "all" && column.errors > 0 && column.ok > 0 && (
                      <line
                        x1={`${column.x}%`}
                        x2={`${column.x + column.width}%`}
                        y1={`${column.split}%`}
                        y2={`${column.split}%`}
                        strokeWidth={2}
                        className="stroke-card"
                      />
                    )}
                    {labelled && (
                      <text
                        x={`${column.centre}%`}
                        y={`${column.top}%`}
                        dy={-6}
                        textAnchor="middle"
                        className="fill-foreground text-xs font-semibold"
                      >
                        {valueText(column.value, shownMeasure)}
                      </text>
                    )}
                  </g>
                ),
            )}
          </svg>
        </div>
        <DayColumnTargets columns={targets} className="absolute inset-x-0 top-5 bottom-0" />
      </div>
      {labelled ? (
        <div className="text-muted mt-2 flex text-xs" aria-hidden="true">
          {columns.map((column) => (
            <span key={column.day} className="flex flex-1 flex-col items-center leading-tight">
              <span>{weekdayOf(column.day)}</span>
              <span className="tabular-nums">{formatDayMonth(column.day)}</span>
            </span>
          ))}
        </div>
      ) : (
        <div className="text-muted mt-2 flex justify-between text-xs" aria-hidden="true">
          <span>{first && dayLabel(first.day)}</span>
          <span>{last && dayLabel(last.day)}</span>
        </div>
      )}
    </figure>
  );
}
